import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ConVersion } from '../../api/cliente';
import type { Cita } from '../../api/ops/citas';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Contexto } from '../../componentes/Contexto';
import { Deshacer } from '../../componentes/Deshacer';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Segmentos } from '../../componentes/Segmentos';
import { rolesDe } from '../../logica/acceso';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { estadoDeError } from '../../logica/estados';
import { sumarDias } from '../../logica/horario';
import { enlaceDeReferencia } from '../../logica/navegacion';
import { useCabecera, type AccionCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { Barra, type Vista } from './Barra';
import { Cifras } from './Cifras';
import type { ContextoAgenda } from './contexto';
import { useDatosAgenda } from './datos';
import { FichaCita } from './Ficha';
import { FormularioCita, type Desde } from './Formulario';
import { ahoraEn, activa, lunesDe, primerNombre, textoDeError } from './logica';
import { MiHorario } from './MiHorario';
import { VentanaNoVino } from './NoVino';
import { AYUDA, PERMISOS, esPestana, type PestanaCita } from './textos';
import { FirmasPendientes, ListaHora, PorCompletar, Rejilla } from './Vistas';
import './agenda.css';

/**
 * «Agenda» (9.1) de Citas, entrada `agenda` del menú con la ruta `/citas` (G779). Una sola entrada para todos:
 *
 * - Propietario, Administrador, Encargado y Recepción: el día por profesional (columnas) o la semana de uno; en móvil y
 *   iPad vertical, la lista por hora con un profesional o todos. Tocar un hueco crea la cita (9.2); tocar una cita abre su
 *   ficha (9.3).
 * - Profesional (`citas.agenda.ver` con «lo suyo», G415): «Mi agenda» (9.8), solo lo suyo, en lista; y «Mi horario» si el
 *   negocio lo deja (G86); si no, «Mi horario» solo lo enseña, con «Pedir un cambio» (G846).
 * - Socio (`citas.agenda.ver_agregado`): solo las cifras (`getAppointmentSummary`).
 *
 * La ficha se abre con `?cita={id}&tab=` (cita, cobro, firma o historial; por defecto, cita), también desde la campana.
 * Nada se completa solo (G14): «Vino» y «No vino» los marca una persona; «No vino», desde el inicio más el margen (G15,
 * G322). «Llegó» y «Vino» llevan «Deshacer» 5 s (G159): la petición sale a los 5 s.
 */
export function Agenda(props: PropsPantalla) {
    const permisos = usePermisos();
    if (!permisos.puede(PERMISOS.ver)) {
        if (permisos.puede(PERMISOS.cifras)) {
            return <Cifras {...props} />;
        }
        return <EstadoSinPermiso seccion="la Agenda" rol={props.membresia === undefined ? '' : rolesDe(props.membresia)} negocio={props.nombreNegocio} onInicio={() => { props.navegar('/'); }} movil={props.movil} />;
    }
    return <AgendaCitas {...props} soloLoSuyo={permisos.ambito(PERMISOS.ver) === 'own'} />;
}

function AgendaCitas(props: PropsPantalla & { soloLoSuyo: boolean }) {
    const { ops, negocio, membresia, movil, soloLectura, busqueda, ruta, navegar, nombreNegocio, soloLoSuyo } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const zona = membresia?.timezone ?? 'Europe/Madrid';
    const [ahora, setAhora] = useState(() => ahoraEn(zona));
    useEffect(() => {
        const t = setInterval(() => {
            setAhora(ahoraEn(zona));
        }, 60_000);
        return () => {
            clearInterval(t);
        };
    }, [zona]);

    const [fecha, setFecha] = useState(() => (/^\d{4}-\d{2}-\d{2}$/.test(busqueda.get('fecha') ?? '') ? (busqueda.get('fecha') ?? ahora.fecha) : ahora.fecha));
    const [vista, setVista] = useState<Vista>(() => (busqueda.get('vista') === 'semana' ? 'semana' : 'dia'));
    const [filtroPro, setFiltroPro] = useState<string>('todos');
    const [proSemana, setProSemana] = useState<string | null>(null);
    const [nueva, setNueva] = useState<Desde | null>(null);
    const [cambiar, setCambiar] = useState<ConVersion<Cita> | null>(null);
    const [notaFicha, setNotaFicha] = useState<{ texto: string; icono: 'send' | 'link' | 'check'; enlace?: string } | null>(null);
    const [noVino, setNoVino] = useState<Cita | null>(null);
    const [horario, setHorario] = useState<{ guardar: (() => void) | null; cambiado: boolean }>({ guardar: null, cambiado: false });

    const desde = vista === 'semana' ? lunesDe(fecha) : fecha;
    const hasta = vista === 'semana' ? sumarDias(desde, 6) : fecha;
    const { estado, recargar, ponerCita } = useDatosAgenda(ops, negocio, desde, hasta, { soloLoSuyo, memberId: membresia?.member_id ?? null, firmas: busqueda.get('firma') === 'pendiente' });
    const datos = estado.tipo === 'listo' ? estado.datos : null;

    const citaAbierta = busqueda.get('cita');
    const tab = busqueda.get('tab');
    const pestana: PestanaCita = esPestana(tab) ? tab : 'cita';
    // «Mi horario» siempre para el profesional con ficha; lo edita solo si el negocio lo deja (G86) y, si no, pide un cambio (G846).
    const puedeHorario = soloLoSuyo && datos !== null && datos.propio !== null;
    const editaHorario = datos?.ajustes?.professional_can_edit_schedule === true;
    const apartado = puedeHorario && busqueda.get('apartado') === 'horario' ? 'horario' : 'citas';
    const base = ruta.split('?')[0] ?? ruta;

    const abrirCita = useCallback(
        (id: string, p: PestanaCita = 'cita') => {
            setNotaFicha(null);
            navegar(`${base}?cita=${encodeURIComponent(id)}&tab=${p}`);
        },
        [navegar, base],
    );
    const cerrarFicha = useCallback(() => {
        setNotaFicha(null);
        navegar(base);
    }, [navegar, base]);

    const puede = useMemo(
        () => ({
            crear: permisos.puede(PERMISOS.gestionar) && !soloLoSuyo,
            gestionar: permisos.puede(PERMISOS.gestionar),
            forzar: permisos.puede(PERMISOS.forzar),
            completar: permisos.puede(PERMISOS.completar),
            noShow: permisos.puede(PERMISOS.noShow),
            deshacerNoShow: permisos.puede(PERMISOS.deshacerNoShow),
            llegada: permisos.puede(PERMISOS.llegada),
            verDocumento: permisos.puede(PERMISOS.verDocumento),
            bloqueados: permisos.puede(PERMISOS.bloqueados),
            recogerFirma: permisos.puede(PERMISOS.recogerFirma),
            reintentarCobro: permisos.puede(PERMISOS.reintentarCobro),
            verPagos: permisos.puede(PERMISOS.verPagos),
            verClientes: permisos.puede(PERMISOS.verClientes),
            crearClientes: permisos.puede(PERMISOS.crearClientes),
        }),
        [permisos, soloLoSuyo],
    );

    const nombreDe = useCallback(
        (c: Pick<Cita, 'customer_id'>) => (c.customer_id === null ? 'Cliente suprimido' : (datos?.nombres[c.customer_id]?.nombre ?? 'Cliente')),
        [datos],
    );

    const ctx: ContextoAgenda | null =
        datos === null
            ? null
            : {
                  ops,
                  negocio,
                  zona,
                  movil,
                  soloLectura,
                  ahora,
                  datos,
                  esProfesional: soloLoSuyo,
                  puede,
                  nombreDe,
                  telefonoDe: (c) => (c.customer_id === null ? null : (datos.nombres[c.customer_id]?.telefono ?? null)),
                  profesional: (id) => datos.profesionales.find((p) => p.id === id) ?? (datos.propio !== null && datos.propio.id === id ? datos.propio : undefined),
                  abrirCita,
                  nuevaCita: (d) => {
                      setNueva(d ?? { fecha: fecha >= ahora.fecha ? fecha : ahora.fecha });
                  },
                  marcarVino: (c) => {
                      envio.programar({
                          texto: `Hecho: ${nombreDe(c)} vino. La cita queda completada.`,
                          donde: 'agenda',
                          enviar: () => {
                              ops.completeAppointment(negocio, c.id)
                                  .then((x) => {
                                      ponerCita(x);
                                      recargar();
                                  })
                                  .catch((e: unknown) => {
                                      avisar({ tipo: 'error', texto: textoDeError(e) });
                                  });
                          },
                      });
                  },
                  registrarLlegada: (c) => {
                      envio.programar({
                          texto: `Llegada de ${nombreDe(c)} registrada a las ${horaAhora(zona)}.`,
                          donde: 'agenda',
                          enviar: () => {
                              ops.listAppointmentPasses(negocio, c.id)
                                  .then((l) => {
                                      const pase = l.data.find((p) => p.status === 'active');
                                      if (pase === undefined) {
                                          throw new Error('sin pase');
                                      }
                                      return ops.registerAppointmentArrival(negocio, pase.id);
                                  })
                                  .then(() => {
                                      recargar();
                                  })
                                  .catch((e: unknown) => {
                                      avisar({ tipo: 'error', texto: textoDeError(e, 'No se ha podido registrar la llegada. Prueba de nuevo.') });
                                  });
                          },
                      });
                  },
                  pedirNoVino: (c) => {
                      setNoVino(c);
                  },
                  actualizada: (c) => {
                      ponerCita(c);
                      recargar();
                  },
                  navegar,
                  rutaDe: (tipo, id) => enlaceDeReferencia(props.destinos ?? [], tipo, id)?.ruta ?? null,
                  ajustes: datos.ajustes,
              };

    // ---- Cabecera: la «?» y el botón principal ----
    const acciones: AccionCabecera[] = [];
    if (estado.tipo === 'listo' && apartado === 'citas' && puede.crear && !soloLectura) {
        acciones.push({ clave: 'nueva', texto: 'Nueva cita', icono: 'calendar-plus', onClick: () => { setNueva({ fecha: fecha >= ahora.fecha ? fecha : ahora.fecha }); } });
    }
    if (estado.tipo === 'listo' && apartado === 'horario' && !soloLectura) {
        acciones.push({ clave: 'horario', texto: 'Guardar horario', icono: 'check', secundaria: !horario.cambiado, onClick: () => { horario.guardar?.(); } });
    }
    const margen = datos?.ajustes?.no_show_margin_minutes;
    const ayudaAgenda = AYUDA.agenda.map((a) => (margen === undefined ? a : { ...a, texto: a.texto.replace('15 minutos', `${String(margen)} minutos`) }));
    useCabecera({ ayuda: apartado === 'horario' ? AYUDA.horario : soloLoSuyo ? AYUDA.mia : movil ? AYUDA.movil : ayudaAgenda, acciones, ...(soloLoSuyo ? { titulo: 'Mi agenda' } : {}) });

    const registrarHorario = useCallback((guardar: (() => void) | null, cambiado: boolean) => {
        setHorario((h) => (h.guardar === guardar && h.cambiado === cambiado ? h : { guardar, cambiado }));
    }, []);

    if (estado.tipo === 'error') {
        const e = estadoDeError(estado.error);
        if (e.tipo === 'sin-permiso') {
            return <EstadoSinPermiso seccion="la Agenda" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
        }
        return <EstadoError seccion="la agenda" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (ctx === null || datos === null) {
        return <EstadoCargando seccion="la agenda" />;
    }

    const listMode = movil || soloLoSuyo;
    const dias = vista === 'semana' ? [0, 1, 2, 3, 4, 5, 6].map((i) => sumarDias(desde, i)) : [fecha];
    const proSemanaSel = datos.profesionales.find((p) => p.id === proSemana) ?? datos.profesionales[0];
    const vaciaDelTodo = datos.citas.filter(activa).length === 0;
    const contar = (id: string) => datos.citas.filter((c) => activa(c) && (vista === 'dia' ? c.local_date === fecha : true) && (id === 'todos' || c.segments.some((s) => s.professional_id === id))).length;
    const filtros = [{ id: 'todos', etiqueta: 'Todos' }, ...datos.profesionales.map((p) => ({ id: p.id, etiqueta: primerNombre(p.display_name) }))];
    const proLista = soloLoSuyo ? datos.propio : filtroPro === 'todos' ? null : (datos.profesionales.find((p) => p.id === filtroPro) ?? null);
    const subtitulo = soloLoSuyo ? (apartado === 'horario' ? 'Los días y las horas en que trabajas.' : 'Tus citas del día.') : 'Las citas del día y de la semana, por profesional.';

    return (
        <div className="rv-ag">
            <p className="rv-ag-sub">{subtitulo}</p>
            {puedeHorario && (
                <div style={{ maxWidth: movil ? undefined : 420 }}>
                    <Segmentos
                        etiqueta="Mi agenda"
                        movil={movil}
                        items={[
                            { etiqueta: 'Mis citas', corta: 'Citas', icono: 'calendar-days' },
                            { etiqueta: 'Mi horario', corta: 'Horario', icono: 'clock' },
                        ]}
                        valor={apartado === 'horario' ? 1 : 0}
                        onCambio={(i) => {
                            navegar(i === 1 ? `${base}?apartado=horario` : base);
                        }}
                    />
                </div>
            )}
            {apartado === 'horario' && datos.propio !== null ? (
                <MiHorario ctx={ctx} propio={datos.propio} editable={editaHorario} registrar={registrarHorario} />
            ) : (
                <>
                    <FirmasPendientes ctx={ctx} />
                    <PorCompletar ctx={ctx} />
                    <Barra fecha={fecha} hoy={ahora.fecha} vista={vista} movil={movil} onFecha={setFecha} onVista={setVista}>
                        {!soloLoSuyo && !movil && vista === 'semana' && proSemanaSel !== undefined && (
                            <Contexto
                                etiqueta="Profesional"
                                icono="user"
                                valor={proSemanaSel.id}
                                opciones={datos.profesionales.map((p) => ({ valor: p.id, texto: primerNombre(p.display_name) }))}
                                onChange={setProSemana}
                            />
                        )}
                    </Barra>
                    {!soloLoSuyo && movil && (
                        <Filtros
                            etiqueta="Profesional"
                            items={filtros.map((f) => ({ etiqueta: f.etiqueta, cifra: contar(f.id) }))}
                            valor={Math.max(0, filtros.findIndex((f) => f.id === filtroPro))}
                            onCambio={(i) => {
                                setFiltroPro(filtros[i]?.id ?? 'todos');
                            }}
                        />
                    )}
                    {vaciaDelTodo ? (
                        <p className="rv-ag-linea">
                            <Icono nombre="inbox" />
                            {puede.crear && !soloLectura ? (vista === 'semana' ? 'No hay citas esta semana. Toca un hueco libre para dar una.' : 'No hay citas este día. Toca un hueco libre para dar una.') : 'No hay citas este día.'}
                        </p>
                    ) : (
                        !soloLoSuyo &&
                        movil &&
                        filtroPro === 'todos' &&
                        vista === 'dia' && (
                            <p className="rv-ag-linea">
                                <Icono nombre="info" />
                                Elige un profesional para ver sus huecos libres.
                            </p>
                        )
                    )}
                    {listMode ? <ListaHora ctx={ctx} dias={dias} profesional={proLista} conTitulo={vista === 'semana'} /> : <Rejilla ctx={ctx} vista={vista} fecha={vista === 'semana' ? desde : fecha} semanaDe={proSemanaSel} />}
                </>
            )}

            {envio.pendiente !== null && (
                <div className="rv-ag-deshacer">
                    <Deshacer texto={envio.pendiente.texto} icono="check" onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                </div>
            )}

            {citaAbierta !== null && nueva === null && cambiar === null && (
                <FichaCita
                    key={citaAbierta}
                    ctx={ctx}
                    id={citaAbierta}
                    pestana={pestana}
                    notaInicial={notaFicha}
                    onPestana={(p) => {
                        abrirCita(citaAbierta, p);
                    }}
                    onCerrar={cerrarFicha}
                    onCambiar={setCambiar}
                />
            )}
            {(nueva !== null || cambiar !== null) && (
                <FormularioCita
                    ctx={ctx}
                    desde={nueva}
                    cita={cambiar}
                    onCerrar={() => {
                        setNueva(null);
                        setCambiar(null);
                    }}
                    onCreada={(c, nota) => {
                        setNueva(null);
                        setCambiar(null);
                        ponerCita(c);
                        recargar();
                        setFecha(c.local_date);
                        // La nota antes de navegar: la ficha la recibe al abrirse.
                        setNotaFicha(nota);
                        navegar(`${base}?cita=${encodeURIComponent(c.id)}&tab=cita`);
                    }}
                />
            )}
            <VentanaNoVino
                ctx={ctx}
                cita={noVino}
                onCerrar={() => {
                    setNoVino(null);
                }}
                onHecho={(c) => {
                    const nombre = noVino === null ? '' : nombreDe(noVino);
                    setNoVino(null);
                    ponerCita(c);
                    recargar();
                    avisar({ tipo: 'ok', texto: `${nombre}: no presentado.` });
                }}
            />
        </div>
    );
}

function horaAhora(zona: string): string {
    const a = ahoraEn(zona);
    return `${String(Math.floor(a.minutos / 60)).padStart(2, '0')}:${String(a.minutos % 60).padStart(2, '0')}`;
}
